import { API_URL } from './env';
import { isDemoData, demoResponse, demoRoleFromCookie } from './demo-data';
import type { PlanCode } from '@content-removal/shared';

/** The signed-in user, as GET /auth/me returns it. */
export interface SessionUser {
  id: string;
  email: string;
  name?: string;
  role: string;
  account?: {
    id: string;
    planCode: PlanCode;
    billingStatus: string;
    extraUsernames: number;
  } | null;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /**
     * The parsed body, for the rare refusal that carries something the page
     * has to act on rather than only show: a 429 saying how many seconds are
     * left is the reason this exists, since a countdown cannot be rebuilt from
     * a sentence. Unknown on purpose — a caller that wants a field out of it
     * says which, and nothing here promises a shape every endpoint has.
     */
    readonly details?: unknown,
  ) {
    super(message);
  }
}

/**
 * Where a session-expired 401 navigates. The fetch layer never navigates itself
 * — redirecting on every 401 loops when a public surface (the login popup) asks
 * something signed out. The authed shell registers this while mounted; a public
 * surface registers nothing, so a 401 there is a value its caller handles.
 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/**
 * The only place either app talks to the API. No token to attach: the session is
 * an httpOnly cookie the browser sends itself, because the API answers on this
 * app's own host. Nothing about it is read into JavaScript — storage APIs are
 * blocked by this audience's privacy tooling, and a token in storage is one
 * injection from stolen. Server messages are surfaced verbatim, so the two
 * cannot drift apart.
 */
/** Gateway statuses nginx returns while an upstream is briefly unreachable. */
const TRANSIENT_STATUSES = new Set([502, 503, 504]);
const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Reads already on their way, by address.
 *
 * One screen asks for the same thing several times because several parts of it
 * need it: the rail wants the account, the page wants the account, a widget
 * inside the page wants the plan. Each was a separate request to the same
 * address, at the same moment, answered identically — on a host whose database
 * pool this product shares with its own scanners.
 *
 * In-flight only, deliberately: nothing is remembered after an answer arrives,
 * so a screen that asks again a second later gets a fresh one. This collapses
 * the pile-up on mount and changes nothing else. Reads only — two POSTs to one
 * address are two different intentions, however alike they look.
 */
const inFlightReads = new Map<string, Promise<unknown>>();

export async function api<T>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const { method = 'GET', body } = options;

  // A demo build has no API: every call is answered from a fixture, see
  // demo-data.ts. Before the in-flight cache, which is only about real requests.
  // The role the demo doors picked rides in a readable (not httpOnly) cookie, so
  // the client sees the same person the server rendered — see demo-login routes.
  if (isDemoData()) return demoResponse<T>(path, method, demoRoleFromCookie());

  if ((method.toUpperCase() === 'GET' || method.toUpperCase() === 'HEAD') && body === undefined) {
    const key = `${method.toUpperCase()} ${path}`;
    const already = inFlightReads.get(key);
    if (already) return already as Promise<T>;
    const started = request<T>(path, options).finally(() => {
      inFlightReads.delete(key);
    });
    inFlightReads.set(key, started);
    return started;
  }

  return request<T>(path, options);
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const { method = 'GET', body } = options;

  /**
   * Retry only reads, and only when the request never reached the app. A deploy
   * can leave a sub-second window where nginx answers 502/503/504, and riding it
   * out is what makes a deploy invisible — but a POST that ran and lost its
   * response would be charged twice on a blind retry, and the client cannot tell
   * "never arrived" from "reply lost". So GET/HEAD only; with the API on a reused
   * port, mutations effectively never meet that window anyway.
   */
  const idempotent = method.toUpperCase() === 'GET' || method.toUpperCase() === 'HEAD';
  const maxAttempts = idempotent ? 3 : 1;

  let response: Response | null = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      response = await fetch(`${API_URL}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        // `same-origin`, which is the default, stated because it used to be
        // `include`: the API lived on a hostname of its own and the cookie had
        // to cross to it. It does not cross anything now, and saying so keeps
        // anyone from restoring the arrangement by accident.
        credentials: 'same-origin',
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch {
      // fetch only rejects when the request never completed: the API is down,
      // DNS failed, or the browser blocked it. Safe to repeat for a read.
      if (attempt < maxAttempts) {
        await wait(attempt * 300);
        continue;
      }
      // "Failed to fetch" tells the person nothing they can act on.
      throw new ApiError('Cannot reach the server. Check your connection and try again.', 0);
    }

    if (TRANSIENT_STATUSES.has(response.status) && attempt < maxAttempts) {
      await wait(attempt * 300);
      continue;
    }
    break;
  }

  // Unreachable: the loop either assigns response or throws, but this satisfies
  // the type and guards a future edit to the loop bounds.
  if (!response) {
    throw new ApiError('Cannot reach the server. Check your connection and try again.', 0);
  }

  return interpret<T>(response, path);
}

/**
 * A file, and whatever text fields go with it. Separate from {@link api} because
 * `Content-Type` must be left unset so the browser can write the multipart
 * boundary, and a helper that sometimes sets a header is one somebody eventually
 * "tidies up". Never retried: sending a client's photograph twice because a reply
 * went missing would put a duplicate in her library at the moment she is deciding
 * whether to trust us with the first.
 */
export async function upload<T>(path: string, form: FormData): Promise<T> {
  // A demo build keeps the file: nothing is uploaded, the screen's own state is
  // what the person sees. See demo-data.ts.
  if (isDemoData()) return { ok: true, demo: true } as T;

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      body: form,
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError('Cannot reach the server. Check your connection and try again.', 0);
  }

  // The body limit is enforced by the server before the route runs, so the
  // refusal arrives without a message of its own.
  if (response.status === 413) {
    throw new ApiError('That file is too large.', 413);
  }

  return interpret<T>(response, path);
}

/**
 * Turn a response into either a value or a message a person can act on.
 *
 * Shared by both callers so a failure reads the same whichever one produced it:
 * one place that knows a non-JSON body is our fault, and one place that decides
 * a lost session ends the page rather than the widget.
 */
async function interpret<T>(response: Response, path: string): Promise<T> {
  const text = await response.text();

  /**
   * A body that is not JSON is a failure of ours, not something to hand over.
   *
   * nginx returning a 502 page, a crash before the exception filter, a gateway
   * timeout: all of them answer with HTML or plain text, and `JSON.parse`
   * throws `Unexpected token 'I', "Internal S"...`. That string then travelled
   * all the way to a customer's screen, where it says nothing except that
   * something is broken in a way we do not understand either.
   */
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      throw new ApiError(
        response.ok
          ? 'The server sent something we could not read. Please try again.'
          : 'Something went wrong on our side. We have been told about it.',
        response.status,
      );
    }
  }

  if (!response.ok) {
    // A 401 outside the auth endpoints means the session is gone. Tell whoever
    // registered a handler (the authed shell) so it can send the reader to
    // sign-in once, rather than every widget rendering "unauthorised" over a
    // list that never loads. No handler (a public surface) means a 401 is just
    // a value the caller deals with. The auth calls own their own 401.
    if (response.status === 401 && !isAuthCall(path)) {
      onUnauthorized?.();
    }

    const body = data as { message?: string | string[] } | null;
    const message = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
    throw new ApiError(message ?? `Request failed (${response.status})`, response.status, data);
  }

  return data as T;
}

/**
 * The endpoints that answer 401 as part of doing their job, rather than because
 * a session ended. A wrong password is a message for the form to show, not a
 * reason to send the person to the page they are already on.
 *
 * Two spellings because the two products knock on different doors:
 * `/auth/login` for customers, `/admin/auth/login` for the support team.
 */
function isAuthCall(path: string): boolean {
  return path.startsWith('/auth/') || path.startsWith('/admin/auth/');
}

/** The current session, or null when signed out. Drives what a page renders;
 *  every endpoint still enforces the role server-side. */
export async function getSession(): Promise<SessionUser | null> {
  try {
    return await api<SessionUser>('/auth/me');
  } catch {
    return null;
  }
}

/** Ends the session by clearing the httpOnly cookie on the server. */
export async function logout(): Promise<void> {
  try {
    await api('/auth/logout', { method: 'POST' });
  } catch {
    // Signing out must never fail the person out of leaving; the redirect that
    // follows takes them away regardless.
  }
}
